/**
 * External icon pack adapter — wraps a JSON manifest into the IconPack
 * interface. Manifests live in `%APPDATA%/AtelierCode/icon-packs/*.json` and
 * are discovered + parsed by the Rust `list_icon_packs` command.
 *
 * SVGs are rendered via dangerouslySetInnerHTML — manifests are user-supplied
 * but only readable from the user's own filesystem (no remote sources), so
 * the trust model matches reading any other file the user owns.
 */
import type { ExternalIconPackManifest, IconPack } from './types';

function fileEntryFor(manifest: ExternalIconPackManifest, fileName: string) {
  const byName = manifest.files?.byName?.[fileName];
  if (byName) return byName;
  const ext = fileName.split('.').pop()?.toLowerCase();
  const byExt = ext ? manifest.files?.byExtension?.[ext] : undefined;
  return byExt ?? manifest.files?.default;
}

function folderEntryFor(manifest: ExternalIconPackManifest, folderName: string) {
  return manifest.folders?.byName?.[folderName] ?? manifest.folders?.default;
}

function renderSvg(svg: string | undefined, color: string | undefined) {
  if (!svg) {
    return <span className="inline-block w-4 h-4" />;
  }
  // Force the SVG to size to 16x16 by wrapping in a fixed-size span.
  return (
    <span
      className="inline-block w-4 h-4"
      style={color ? { color } : undefined}
      // Manifest contents come from local disk (the user's own filesystem),
      // not the network — this is the same trust level as reading any file.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export function packFromManifest(manifest: ExternalIconPackManifest): IconPack {
  return {
    id: manifest.id,
    name: manifest.name,
    description: manifest.description ?? 'External icon pack',
    external: true,

    getFileIcon(fileName) {
      const entry = fileEntryFor(manifest, fileName);
      return renderSvg(entry?.svg, entry?.color);
    },

    getFolderIcon(folderName, open) {
      const entry = folderEntryFor(manifest, folderName);
      const svg = open ? entry?.svgOpen ?? entry?.svgClosed : entry?.svgClosed;
      return renderSvg(svg, entry?.color);
    },
  };
}
